/**
 * The visual identity of a taxon or trait in every chart: one of 25 colours plus one of four marks,
 * both a pure function of the name. Nothing depends on siblings, data or filters, so a name looks
 * the same in every chart, project and export.
 *
 * Pure and DOM-free: the chart option builders, the HTML legend/tooltip keys and the chart-render
 * sidecar all draw from this one module.
 */

/**
 * Optimised for the largest worst-pair OKLab distance inside a readable lightness/chroma band (worst
 * pair ΔE ≈ 12.9 normal vision, 4.3 colour-blind), so the marks carry identity for colour-blind
 * readers. Order is part of the hash contract: append only, never reorder or remove.
 */
export const IDENTITY_COLORS = [
  '#9294fa',
  '#abbb68',
  '#fe1400',
  '#8f0070',
  '#1042f8',
  '#f957e0',
  '#7b4000',
  '#22823d',
  '#ba04b3',
  '#8603de',
  '#fc758b',
  '#b94dff',
  '#40bf21',
  '#59399f',
  '#2292bf',
  '#e20378',
  '#b965ad',
  '#f39534',
  '#b30026',
  '#006294',
  '#24ac86',
  '#bf5d4c',
  '#7564d0',
  '#28c1fa',
  '#9a7f10'
]

export const CHART_SURFACE = '#ffffff'

/** Placeholders never take a palette colour; they read as "not a real taxon". */
const RESERVED: Record<string, string> = {
  nan: '#9ca3af',
  '(n/a)': '#9ca3af',
  other: '#9ca3af',
  '(no trait data)': '#787276'
}

export type Mark = 'solid' | 'bands' | 'stagger' | 'hatch'

/** Hash contract: append only. */
export const MARKS: Mark[] = ['solid', 'bands', 'stagger', 'hatch']

export interface Identity {
  name: string
  color: string
  mark: Mark
  reserved: boolean
}

function fnv1a(key: string, seed: number): number {
  let hash = (0x811c9dc5 ^ seed) >>> 0
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/** Lamping & Veach jump consistent hash: appending a colour moves only ~1/n of names. */
export function jumpHash(key: number, buckets: number): number {
  let k = BigInt(key)
  let b = -1n
  let j = 0n
  while (j < BigInt(buckets)) {
    b = j
    k = (k * 2862933555777941757n + 1n) & 0xffffffffffffffffn
    j = BigInt(Math.floor(((Number(b) + 1) * 2 ** 31) / Number((k >> 33n) + 1n)))
  }
  return Number(b)
}

/**
 * How marks are drawn. Production uses the defaults; /colorcode/lab can override them to compare looks.
 * `solids: 'heavy'` changes which mark a name gets, so it is part of the hash contract once chosen.
 */
export interface IdentityStyle {
  tone: 'contrast' | 'tonal' | 'black'
  density: 'dense' | 'sparse'
  /** Fills smaller than this share of the axis (or of the whole, for pie/treemap) stay plain. */
  minShare: number
  solids: 'uniform' | 'heavy'
}

export const DEFAULT_IDENTITY_STYLE: IdentityStyle = { tone: 'tonal', density: 'sparse', minShare: 0, solids: 'uniform' }

let style: IdentityStyle = DEFAULT_IDENTITY_STYLE

export const identityStyle = (): IdentityStyle => style

export function setIdentityStyle(next: Partial<IdentityStyle> = {}): void {
  style = { ...DEFAULT_IDENTITY_STYLE, ...next }
  cache.clear()
}

const HEAVY_SOLID_MARKS: Mark[] = ['solid', 'solid', 'solid', 'bands', 'stagger', 'hatch']

const cache = new Map<string, Identity>()

export function identityFor(name: string): Identity {
  const cached = cache.get(name)
  if (cached) return cached
  const reservedColor = RESERVED[name.trim().toLowerCase()]
  const identity: Identity = reservedColor
    ? { name, color: reservedColor, mark: 'solid', reserved: true }
    : {
        name,
        color: IDENTITY_COLORS[jumpHash(fnv1a(name, 0), IDENTITY_COLORS.length)]!,
        mark:
          style.solids === 'heavy'
            ? HEAVY_SOLID_MARKS[fnv1a(name, 0x9e3779b9) % HEAVY_SOLID_MARKS.length]!
            : MARKS[fnv1a(name, 0x9e3779b9) % MARKS.length]!,
        reserved: false
      }
  cache.set(name, identity)
  return identity
}

/** Colour of the marks drawn over [primary]. */
export function markColor(primary: string): string {
  if (style.tone === 'black') return 'rgba(0,0,0,0.6)'
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(primary.slice(i, i + 2), 16) / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  const luminance = 0.2126 * r! + 0.7152 * g! + 0.0722 * b!
  // Tonal: a shade of the same colour. A darker shade vanishes on dark colours, so those lighten.
  if (style.tone === 'tonal') return luminance > 0.18 ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.3)'
  return luminance > 0.3 ? 'rgba(17,24,39,0.55)' : 'rgba(255,255,255,0.75)'
}

/**
 * Fill geometry in px, shared by the echarts decals and the SVG keys so the two cannot drift.
 * Bands run horizontally, so a bar only a few px wide still shows them along its height.
 */
const DENSE = { band: 2, bandPeriod: 10, dot: 2, dotPeriod: 6, hatch: 2, hatchPeriod: 7 }
const SPARSE = { band: 1.5, bandPeriod: 14, dot: 1.6, dotPeriod: 10, hatch: 1.5, hatchPeriod: 11 }
const fill = () => (style.density === 'sparse' ? SPARSE : DENSE)

/** echarts `decal` for a fill; undefined for solid. */
export function decalFor(identity: Identity) {
  const F = fill()
  const color = markColor(identity.color)
  switch (identity.mark) {
    case 'solid':
      return undefined
    case 'bands':
      return { symbol: 'rect', symbolSize: 1, dashArrayX: [1, 0], dashArrayY: [F.band, F.bandPeriod - F.band], rotation: 0, color }
    case 'stagger': {
      // Alternate rows shift by half a period, so the dots read as scattered rather than a grid.
      const half = F.dotPeriod / 2
      return {
        symbol: 'circle',
        symbolSize: 1,
        dashArrayX: [
          [F.dot, F.dotPeriod - F.dot],
          [0, half, F.dot, half - F.dot]
        ],
        dashArrayY: [F.dot, half - F.dot],
        rotation: 0,
        color
      }
    }
    case 'hatch':
      return {
        symbol: 'rect',
        symbolSize: 1,
        dashArrayX: [1, 0],
        dashArrayY: [F.hatch, F.hatchPeriod - F.hatch],
        rotation: Math.PI / 4,
        color
      }
  }
}

/**
 * echarts `itemStyle` for anything filled: bars, areas, pie slices, treemap tiles. [share] is the
 * fill's size relative to the axis or whole; too small a fill cannot show a mark, only noise.
 */
export const fillStyleFor = (identity: Identity, share = 1) =>
  share < style.minShare ? { color: identity.color } : { color: identity.color, decal: decalFor(identity) }

export interface LineMark {
  type: 'solid' | number[]
  cap: 'butt' | 'round'
}

/** Dashes and dots are the primary colour, the gaps show the surface; all scale with the width. */
export function lineMarkFor(identity: Identity, width: number): LineMark {
  switch (identity.mark) {
    case 'solid':
      return { type: 'solid', cap: 'butt' }
    case 'bands':
      return { type: [5 * width, 2 * width], cap: 'butt' }
    // A near-zero dash with round caps draws a dot exactly one line-width across.
    case 'stagger':
      return { type: [0.01, 2.2 * width], cap: 'round' }
    case 'hatch':
      return { type: [4 * width, 2 * width, 0.01, 2 * width], cap: 'round' }
  }
}

/** echarts `lineStyle` for a line series. */
export const lineStyleFor = (identity: Identity, width: number) => ({ color: identity.color, width, ...lineMarkFor(identity, width) })

function patternSvg(id: string, identity: Identity): string {
  const F = fill()
  const c = identity.color
  const m = markColor(c)
  switch (identity.mark) {
    case 'solid':
      return ''
    case 'bands':
      return `<pattern id="${id}" width="4" height="${F.bandPeriod}" patternUnits="userSpaceOnUse"><rect width="4" height="${F.bandPeriod}" fill="${c}"/><rect width="4" height="${F.band}" fill="${m}"/></pattern>`
    case 'stagger': {
      const p = F.dotPeriod
      const r = F.dot / 2
      return `<pattern id="${id}" width="${p}" height="${p}" patternUnits="userSpaceOnUse"><rect width="${p}" height="${p}" fill="${c}"/><circle cx="${r}" cy="${r}" r="${r}" fill="${m}"/><circle cx="${p / 2 + r}" cy="${p / 2 + r}" r="${r}" fill="${m}"/></pattern>`
    }
    case 'hatch':
      return `<pattern id="${id}" width="4" height="${F.hatchPeriod}" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="4" height="${F.hatchPeriod}" fill="${c}"/><rect width="4" height="${F.hatch}" fill="${m}"/></pattern>`
  }
}

export interface KeyOptions {
  /** Edge of the fill square in px; 0 draws only the line sample. */
  size?: number
  /** Line sample length in px; 0 hides it. Long enough for one dash-dot cycle by default. */
  lineLength?: number
  lineWidth?: number
  /**
   * Appended to the pattern id. url(#id) resolves to the first element with that id in the document, so markup
   * that may share a page with other keys of the same identity (a hidden chart tooltip) needs its own.
   */
  idScope?: string
}

/**
 * Legend/tooltip key as SVG markup: the fill as a square, then a line sample, so a name is
 * recognisable in fill charts and line charts alike. Same markup for React (IdentityKey) and for
 * echarts' HTML tooltips.
 */
export function identityKeySvg(identity: Identity, { size = 12, lineLength = 34, lineWidth = 2.5, idScope }: KeyOptions = {}): string {
  const id = `vi-${identity.color.replace('#', '')}-${identity.mark}-${style.tone}-${style.density}${idScope ? `-${idScope}` : ''}`
  const fill = identity.mark === 'solid' ? identity.color : `url(#${id})`
  const gap = size > 0 && lineLength > 0 ? 4 : 0
  const width = size + gap + lineLength
  const height = Math.max(size, lineWidth + 2)
  const { type, cap } = lineMarkFor(identity, lineWidth)
  const dash = type === 'solid' ? '' : ` stroke-dasharray="${type.join(' ')}"`
  const square = size > 0 ? `<rect width="${size}" height="${size}" y="${(height - size) / 2}" rx="2" fill="${fill}"/>` : ''
  const line =
    lineLength > 0
      ? `<line x1="${size + gap + lineWidth / 2}" x2="${width - lineWidth / 2}" y1="${height / 2}" y2="${height / 2}" stroke="${identity.color}" stroke-width="${lineWidth}" stroke-linecap="${cap}"${dash}/>`
      : ''
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" style="display:inline-block;vertical-align:middle;flex-shrink:0"><defs>${patternSvg(id, identity)}</defs>${square}${line}</svg>`
}
